import type { Unit } from '@/types/v2/unit';
import type { User } from '@/types/v2/user';
import axios from '@/lib/v2/axios';

export async function getUnit(id: string, params: { include?: string } = {}): Promise<Unit> {
  try {
    const response = await axios.get(`/api/v2/units/${id}`, { params });
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      throw new Error('Unit not found');
    }
    throw error;
  }
}

export async function getUnits(params: {
  include?: string;
  include_count?: string;
  sort?: string;
  current_owner?: boolean;
  exclude_current_owners?: boolean;
  vacant?: boolean;
  per_page?: number;
  page?: number;
} = {}): Promise<{ units: Unit[]; meta: any }> {
  const response = await axios.get(`/api/v2/units`, { params });
  return response.data;
}

export async function getAllUnits(params: {
  include?: string;
  include_count?: string;
  sort?: string;
  current_owner?: boolean;
  exclude_current_owners?: boolean;
  vacant?: boolean;
  per_page?: number;
} = {}): Promise<{ units: Unit[] }> {
  // Fetch all units by setting a very high per_page limit
  const response = await axios.get(`/api/v2/units`, { 
    params: { 
      ...params, 
      all: true // Large number to get all units
    } 
  });
  return response.data;
}

export async function getUnitOwners(
  unitId: string,
  params: { include?: string; sort?: string; current_owner?: boolean; exclude_current_owners?: boolean } = {}
): Promise<{ owners: User[] }> {
  const url = `/api/v2/units/${unitId}/owners/history`;
  const response = await axios.get(url, { params });
  return response.data;
}

export async function removeUserFromUnit(userId: string, unitId: string): Promise<void> {
  await axios.delete(`/api/v1/users/${userId}/units/${unitId}`);
}

export async function updateUnit(id: string, data: {
  unit_number?: string;
  address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  floor?: string;
}): Promise<Unit> {
  const response = await axios.put(`/api/v2/units/${id}`, data);
  return response.data;
}

export async function deleteUnit(id: string): Promise<void> {
  await axios.delete(`/api/v2/units/${id}`);
}

export async function bulkDeleteUnits(unitIds: string[]): Promise<{
  success: boolean;
  message: string;
  details: {
    deleted_count: number;
    errors: string[];
  };
}> {
  const response = await axios.delete('/api/v2/units/bulk-delete', {
    data: { unit_ids: unitIds }
  });
  return response.data;
}
